/**
 * Awards showcase: exclusive reveal on hover (desktop) and tap (mobile),
 * with a transform-based infinite track on mobile (Safari-safe) and
 * half-item edge peeks. Targets elements with [data-awards-root].
 */

/**
 * @param {HTMLElement} root
 */
function initAwardsRoot(root) {
  if (!root || root.dataset.awardsInitialized === 'true') {
    return;
  }

  root.dataset.awardsInitialized = 'true';

  const grid = root.querySelector('.awards__grid');
  const viewport = root.querySelector('.awards__viewport') || grid;
  const fineHoverQuery = window.matchMedia(
    '(hover: hover) and (pointer: fine)',
  );
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  /**
   * @typedef {{
   *   setWidth: number,
   *   peekOffset: number,
   *   originalCount: number,
   *   offset: number,
   *   velocity: number,
   *   isDragging: boolean,
   *   didDrag: boolean,
   *   suppressClick: boolean,
   *   startX: number,
   *   startY: number,
   *   lastX: number,
   *   lastTime: number,
   *   rafId: number | null,
   *   pointerId: number | null,
   *   axisLocked: 'x' | 'y' | null,
   * }} AwardsSliderState
   */

  /** @type {AwardsSliderState | null} */
  let sliderState = null;

  /**
   * @returns {HTMLElement[]}
   */
  function getDirectItems() {
    if (!grid) {
      return [];
    }
    return Array.prototype.filter.call(grid.children, function (child) {
      return child.classList && child.classList.contains('awards__item');
    });
  }

  /**
   * @returns {HTMLElement[]}
   */
  function getAwardItems() {
    return Array.prototype.slice.call(
      root.querySelectorAll('[data-awards-item]'),
    );
  }

  /**
   * Map any item (original or clone) to its award index in the set.
   *
   * @param {HTMLElement} item
   * @returns {number}
   */
  function getAwardIndex(item) {
    if (item.dataset.awardsIndex != null && item.dataset.awardsIndex !== '') {
      const indexed = parseInt(item.dataset.awardsIndex, 10);
      if (!isNaN(indexed)) {
        return indexed;
      }
    }

    const items = getDirectItems();
    const index = items.indexOf(item);
    if (index < 0) {
      return -1;
    }
    const count = sliderState
      ? sliderState.originalCount
      : items.filter(function (entry) {
        return entry.dataset.awardsClone !== 'true';
      }).length;
    if (!count) {
      return index;
    }
    return index % count;
  }

  /**
   * Open the award at a given index across originals and clones so
   * edge peeks / cloned slides reveal the same content.
   *
   * @param {HTMLElement | null} target
   */
  function setOpenItem(target) {
    const openIndex = target ? getAwardIndex(target) : -1;

    getAwardItems().forEach(function (item) {
      const trigger = item.querySelector('[data-awards-trigger]');
      const isClone = item.dataset.awardsClone === 'true';
      const isOpen = openIndex >= 0 && getAwardIndex(item) === openIndex;
      item.classList.toggle('is-open', isOpen);
      if (trigger && !isClone) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    });
  }

  function clearOpen() {
    setOpenItem(null);
  }

  /**
   * Toggle reveal for the interactive award under a screen point.
   * Used for mobile taps so Safari overflow/transform hit-testing
   * and drag-vs-click races do not swallow the reveal.
   *
   * @param {number} clientX
   * @param {number} clientY
   * @returns {boolean}
   */
  function toggleItemAtPoint(clientX, clientY) {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el || !root.contains(el)) {
      return false;
    }

    const trigger = el.closest('[data-awards-trigger]');
    if (!trigger || !root.contains(trigger)) {
      return false;
    }

    const item = trigger.closest('[data-awards-item]');
    if (!item) {
      return false;
    }

    if (item.classList.contains('is-open')) {
      clearOpen();
    } else {
      setOpenItem(item);
    }
    return true;
  }

  root.addEventListener('click', function (event) {
    if (sliderState && sliderState.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      sliderState.suppressClick = false;
      return;
    }

    // Mobile slider handles taps on pointerup.
    if (sliderState && mobileQuery.matches) {
      return;
    }

    const trigger = event.target.closest('[data-awards-trigger]');
    if (!trigger || !root.contains(trigger)) {
      return;
    }

    const item = trigger.closest('[data-awards-item]');
    if (!item) {
      return;
    }

    event.preventDefault();
    if (item.classList.contains('is-open')) {
      clearOpen();
      return;
    }
    setOpenItem(item);
  });

  root.addEventListener('mouseover', function (event) {
    if (!fineHoverQuery.matches) {
      return;
    }
    const item = event.target.closest('[data-awards-item]');
    if (!item || !root.contains(item) || item.dataset.awardsClone === 'true') {
      return;
    }
    setOpenItem(item);
  });

  root.addEventListener('focusin', function (event) {
    const trigger = event.target.closest('[data-awards-trigger]');
    if (!trigger || !root.contains(trigger)) {
      return;
    }
    if (trigger.matches && !trigger.matches(':focus-visible')) {
      return;
    }
    const item = trigger.closest('[data-awards-item]');
    if (item && item.dataset.awardsClone !== 'true') {
      setOpenItem(item);
    }
  });

  document.addEventListener('pointerdown', function (event) {
    if (!root.querySelector('.is-open')) {
      return;
    }
    if (root.contains(event.target)) {
      return;
    }
    clearOpen();
  });

  root.addEventListener('mouseleave', function () {
    if (!fineHoverQuery.matches) {
      return;
    }
    clearOpen();
  });

  root.addEventListener('focusout', function (event) {
    const next = event.relatedTarget;
    if (next && root.contains(next)) {
      return;
    }
    if (fineHoverQuery.matches) {
      clearOpen();
    }
  });

  if (!grid) {
    return;
  }

  /**
   * Decorative infinite-track clone. Hidden from AT and out of the
   * tab order, but still tappable so edge peeks can reveal content.
   *
   * @param {HTMLElement} item
   * @param {string} suffix
   * @returns {HTMLElement}
   */
  function cloneItem(item, suffix) {
    const clone = item.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.dataset.awardsClone = 'true';

    clone.querySelectorAll('[id]').forEach(function (el) {
      el.id = el.id + suffix;
    });
    clone.querySelectorAll('[aria-controls]').forEach(function (el) {
      const controls = el.getAttribute('aria-controls');
      if (controls) {
        el.setAttribute('aria-controls', controls + suffix);
      }
    });

    const focusableSelector = [
      'button',
      'a[href]',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');
    clone.querySelectorAll(focusableSelector).forEach(function (el) {
      el.setAttribute('tabindex', '-1');
      el.removeAttribute('aria-expanded');
    });

    return clone;
  }

  /**
   * Keep offset inside the middle set band without a visible jump.
   *
   * @param {number} offset
   * @returns {number}
   */
  function wrapOffset(offset) {
    if (!sliderState || !sliderState.setWidth) {
      return offset;
    }
    const setWidth = sliderState.setWidth;
    let next = offset;
    while (next < setWidth) {
      next += setWidth;
    }
    while (next >= setWidth * 2) {
      next -= setWidth;
    }
    return next;
  }

  /**
   * Apply translate3d. Wrapping happens in the same frame so Safari
   * never paints a seam jump the way native scrollLeft teleports do.
   *
   * @param {number} offset
   * @param {boolean} [shouldWrap]
   */
  function applyOffset(offset, shouldWrap) {
    if (!sliderState || !grid) {
      return;
    }
    const next = shouldWrap === false ? offset : wrapOffset(offset);
    sliderState.offset = next;
    grid.style.transform = 'translate3d(' + (-next) + 'px, 0, 0)';
  }

  function measureSlider() {
    if (!sliderState) {
      return;
    }

    const items = getDirectItems();
    const count = sliderState.originalCount;
    const middleStart = items[count];
    const middleEnd = items[count * 2];

    if (!middleStart) {
      return;
    }

    // offsetLeft is layout-based and ignores the track transform.
    if (middleEnd) {
      sliderState.setWidth = Math.round(
        middleEnd.offsetLeft - middleStart.offsetLeft,
      );
    } else {
      const itemWidth = middleStart.offsetWidth;
      const gridStyles = window.getComputedStyle(grid);
      const gridGap = parseFloat(gridStyles.columnGap)
        || parseFloat(gridStyles.gap)
        || 0;
      sliderState.setWidth = Math.round((itemWidth + gridGap) * count);
    }

    sliderState.peekOffset = Math.round(middleStart.offsetWidth / 2);
  }

  function jumpToMiddleSet() {
    if (!sliderState || !sliderState.setWidth) {
      return;
    }
    applyOffset(sliderState.setWidth + sliderState.peekOffset, false);
  }

  function stopMomentum() {
    if (!sliderState || sliderState.rafId === null) {
      return;
    }
    cancelAnimationFrame(sliderState.rafId);
    sliderState.rafId = null;
  }

  function tickMomentum() {
    if (!sliderState) {
      return;
    }

    sliderState.velocity *= 0.95;
    if (Math.abs(sliderState.velocity) < 0.08) {
      sliderState.velocity = 0;
      sliderState.rafId = null;
      applyOffset(sliderState.offset);
      return;
    }

    applyOffset(sliderState.offset + sliderState.velocity);
    sliderState.rafId = requestAnimationFrame(tickMomentum);
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerDown(event) {
    if (!sliderState || !mobileQuery.matches) {
      return;
    }
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    stopMomentum();
    sliderState.isDragging = true;
    sliderState.didDrag = false;
    sliderState.suppressClick = false;
    sliderState.axisLocked = null;
    sliderState.pointerId = event.pointerId;
    sliderState.startX = event.clientX;
    sliderState.startY = event.clientY;
    sliderState.lastX = event.clientX;
    sliderState.lastTime = event.timeStamp;
    sliderState.velocity = 0;
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerMove(event) {
    if (!sliderState || !sliderState.isDragging) {
      return;
    }
    if (
      sliderState.pointerId !== null
      && event.pointerId !== sliderState.pointerId
    ) {
      return;
    }

    if (sliderState.axisLocked === 'y') {
      return;
    }

    const dxFromStart = event.clientX - sliderState.startX;
    const dyFromStart = event.clientY - sliderState.startY;
    const dragThreshold = 10;

    if (sliderState.axisLocked === null) {
      if (
        Math.abs(dxFromStart) < dragThreshold
        && Math.abs(dyFromStart) < dragThreshold
      ) {
        return;
      }
      if (Math.abs(dyFromStart) > Math.abs(dxFromStart)) {
        sliderState.axisLocked = 'y';
        sliderState.isDragging = false;
        sliderState.pointerId = null;
        return;
      }
      sliderState.axisLocked = 'x';
      sliderState.lastX = event.clientX;
      sliderState.lastTime = event.timeStamp;
      if (viewport.setPointerCapture) {
        viewport.setPointerCapture(event.pointerId);
      }
    }

    const dx = event.clientX - sliderState.lastX;
    const dt = Math.max(event.timeStamp - sliderState.lastTime, 1);

    if (Math.abs(dx) < 1 && !sliderState.didDrag) {
      return;
    }

    sliderState.didDrag = true;
    event.preventDefault();

    // Finger right → content right → offset decreases.
    applyOffset(sliderState.offset - dx);
    sliderState.velocity = (-dx) / dt * 16;
    sliderState.lastX = event.clientX;
    sliderState.lastTime = event.timeStamp;
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerUp(event) {
    if (!sliderState) {
      return;
    }
    if (
      sliderState.pointerId !== null
      && event.pointerId !== sliderState.pointerId
    ) {
      return;
    }

    const axis = sliderState.axisLocked;
    const didDrag = sliderState.didDrag;
    const startX = sliderState.startX;
    const startY = sliderState.startY;
    const totalMove = Math.hypot(
      event.clientX - startX,
      event.clientY - startY,
    );
    const wasTap = !didDrag && axis !== 'y' && totalMove < 12;

    sliderState.isDragging = false;
    sliderState.pointerId = null;
    sliderState.axisLocked = null;

    if (viewport.releasePointerCapture && viewport.hasPointerCapture) {
      if (viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
    }

    if (wasTap) {
      sliderState.suppressClick = true;
      toggleItemAtPoint(event.clientX, event.clientY);
      return;
    }

    if (didDrag) {
      sliderState.suppressClick = true;
      applyOffset(sliderState.offset);

      if (Math.abs(sliderState.velocity) > 0.4) {
        stopMomentum();
        sliderState.rafId = requestAnimationFrame(tickMomentum);
      }
    }
  }

  function onSliderResize() {
    if (!mobileQuery.matches || !sliderState) {
      return;
    }
    stopMomentum();
    measureSlider();
    jumpToMiddleSet();
  }

  function initMobileInfiniteSlider() {
    if (sliderState) {
      measureSlider();
      jumpToMiddleSet();
      return;
    }

    const originalItems = getDirectItems().filter(function (item) {
      return item.dataset.awardsClone !== 'true';
    });
    if (originalItems.length < 2) {
      return;
    }

    originalItems.forEach(function (item, index) {
      item.dataset.awardsIndex = String(index);
    });

    originalItems.forEach(function (item, index) {
      grid.appendChild(cloneItem(item, '-clone-a-' + index));
    });

    const firstOriginal = originalItems[0];
    originalItems.forEach(function (item, index) {
      grid.insertBefore(
        cloneItem(item, '-clone-b-' + index),
        firstOriginal,
      );
    });

    sliderState = {
      setWidth: 0,
      peekOffset: 0,
      originalCount: originalItems.length,
      offset: 0,
      velocity: 0,
      isDragging: false,
      didDrag: false,
      suppressClick: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastTime: 0,
      rafId: null,
      pointerId: null,
      axisLocked: null,
    };

    root.classList.add('awards--slider-active');
    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove, {passive: false});
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    window.addEventListener('resize', onSliderResize);

    measureSlider();
    jumpToMiddleSet();
    requestAnimationFrame(function () {
      measureSlider();
      jumpToMiddleSet();
    });
  }

  function destroyMobileInfiniteSlider() {
    if (!sliderState) {
      return;
    }

    stopMomentum();
    viewport.removeEventListener('pointerdown', onPointerDown);
    viewport.removeEventListener('pointermove', onPointerMove);
    viewport.removeEventListener('pointerup', onPointerUp);
    viewport.removeEventListener('pointercancel', onPointerUp);
    window.removeEventListener('resize', onSliderResize);

    getDirectItems().forEach(function (item) {
      if (item.dataset.awardsClone === 'true') {
        item.remove();
      }
    });

    grid.style.transform = '';
    root.classList.remove('awards--slider-active');
    sliderState = null;
  }

  function syncInfiniteSlider() {
    if (mobileQuery.matches) {
      initMobileInfiniteSlider();
    } else {
      destroyMobileInfiniteSlider();
    }
  }

  syncInfiniteSlider();

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', syncInfiniteSlider);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(syncInfiniteSlider);
  }
}

/**
 * Initialise all awards roots on the page.
 */
function initAllAwards() {
  document.querySelectorAll('[data-awards-root]').forEach(initAwardsRoot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllAwards);
} else {
  initAllAwards();
}

document.addEventListener('shopify:section:load', function () {
  document.querySelectorAll('[data-awards-root]').forEach(function (root) {
    delete root.dataset.awardsInitialized;
  });
  initAllAwards();
});
